/**
 * Authentication Context Module
 * 
 * Provides authentication state management and utilities for the application.
 * Handles user login, logout, session management, and inactivity timeouts.
 * 
 * @module AuthContext
 */
import React, { createContext, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Cookies from 'js-cookie';
import { jwtDecode } from 'jwt-decode';
import { getStoredCarts, saveStoredCarts } from '../services/cartService';
import { AuthUser } from '../Interface/contextInterface';
import { AuthContextType } from '../Interface/contextInterface';
import { closeAllModals } from '../utils/modalsUtils';

/** Inactivity timeout for regular users (60 minutes) */
const INACTIVITY_TIMEOUT = 60 * 60 * 1000;
/** Inactivity timeout for admin users (60 minutes) */
const INACTIVITY_TIMEOUT_ADMIN = 60 * 60 * 1000;

const TIME_EXPIRED_TOKEN = 3600;

/**
 * Interface for decoded JWT token
 */
interface DecodedToken {
  /** Token expiration timestamp */
  exp: number;
  [key: string]: any;
}

/**
 * Authentication context instance
 */
export const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Authentication Provider component
 * 
 * @component
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to be wrapped by the provider
 * @returns {JSX.Element} Authentication context provider
 */
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(() => {
    const token = Cookies.get('token');

    if (token) {
      const username = localStorage.getItem('username');
      const userId = localStorage.getItem('userId');
      const role = localStorage.getItem('role');
      const currentTime = Date.now() / 1000; // en segundos
        const lastLogin = localStorage.getItem('lastLogin');
        if (lastLogin && role !== 'admin') {
          const lastLoginTime = Number(lastLogin);
          const oneHourLater = lastLoginTime + TIME_EXPIRED_TOKEN;
          if (currentTime > oneHourLater) {
            return null; // Forzar logout si el token ha expirado
          }
        }

      return token && username && userId && role
        ? { token, username, userId: Number(userId), role }
        : null;
    }
    return null;
  });

  const navigate = useNavigate();
  const location = useLocation();
  const inactivityTimer = useRef<NodeJS.Timeout | null>(null);

  /**
 * Logs out the current user
 * - Clears user state
 * - Removes authentication token
 * - Closes all modals
 * - Redirects to login page
 */
const logout = useCallback(() => {
    setUser(null);
    Cookies.remove('token');
    closeAllModals();
    navigate('/authentication/login');
  }, [navigate]);

  /**
 * Resets the inactivity timer
 * - Clears existing timeout
 * - Sets new timeout based on user role
 * - Triggers logout when timeout expires
 */
const resetInactivityTimer = useCallback(() => {
    if (inactivityTimer.current) {
      clearTimeout(inactivityTimer.current);
    }
    const timeout = user?.role === 'admin' ? INACTIVITY_TIMEOUT_ADMIN : INACTIVITY_TIMEOUT;
    inactivityTimer.current = setTimeout(() => {
      logout();
    }, timeout);
  }, [logout, user?.role]);

  useEffect(() => {
    const token = Cookies.get('token');
    if (token && user?.role !== 'admin' ) {
      try {
        const currentTime = Date.now() / 1000; // en segundos
        const lastLogin = localStorage.getItem('lastLogin');
        if (lastLogin) {
          const lastLoginTime = Number(lastLogin);
          const oneHourLater = lastLoginTime + TIME_EXPIRED_TOKEN;
          if (currentTime > oneHourLater) {
            logout();
            return;
          }
        } else {
          logout();
          return;
        }
      } catch (error) {
        logout(); // Token mal formado, forzar logout
        return;
      }
    }

    resetInactivityTimer();

    const events = ['mousemove', 'keydown', 'scroll'];
    events.forEach(event => window.addEventListener(event, resetInactivityTimer));

    return () => {
      if (inactivityTimer.current) {
        clearTimeout(inactivityTimer.current);
      }
      events.forEach(event => window.removeEventListener(event, resetInactivityTimer));
    };
  }, [user, resetInactivityTimer, logout]);

  /**
 * Handles user login
 * 
 * @param {AuthUser} userData - User authentication data
 * - Sets user state
 * - Stores token in cookies
 * - Saves user data in localStorage
 * - Resets inactivity timer
 * - Merges guest cart with user cart
 * - Redirects based on user role
 */
const login = (userData: AuthUser) => {
    setUser(userData);
    const decoded = jwtDecode<DecodedToken>(userData.token);

    // Usamos maxAge para que expire en 1 hora (3600 segundos)
    Cookies.set('token', userData.token, {
      expires: 1,
      secure: false,
      sameSite: 'Lax',
    });

    localStorage.setItem('userId', userData.userId.toString());
    localStorage.setItem('username', userData.username);
    localStorage.setItem('lastLogin', decoded.iat);
    localStorage.setItem('role', userData.role);

    resetInactivityTimer();
    mergeCartWithUser(userData.userId);

    if (location.pathname === '/perfil/cart') return;

    switch (userData.role) {
      case 'admin':
        navigate('/admin');
        break;
      case 'trainer':
        navigate('/perfil/customer-dashboard');
        break;
      case 'receptionist':
        navigate('/perfil/receptionist-payment');
        break;
      default:
        if (userData.isSubscribed) {
          navigate('/perfil/agenda');
        } else {
          navigate('/#package-section');
        }
    }
  };

  /**
 * Merges guest cart with user's cart
 * 
 * @param {number} userId - ID of the logged-in user
 */
const mergeCartWithUser = (userId: number) => {
    const carts = getStoredCarts();
    const guestCart = carts['general'] || [];
    const userCart = carts[userId.toString()] || [];

    const mergedCart = [...userCart];

    guestCart.forEach((guestProduct) => {
      const existingProduct = mergedCart.find((product) => product.id === guestProduct.id);
      if (existingProduct) {
        existingProduct.quantity += guestProduct.quantity;
      } else {
        mergedCart.push(guestProduct);
      }
    });

    carts[userId.toString()] = mergedCart;
    delete carts['general'];
    saveStoredCarts(carts);
  };

  /**
 * Updates user data
 * 
 * @param {Partial<AuthUser>} updates - Partial user data to update
 */
const updateUserData = (updates: Partial<AuthUser>) => {
    setUser((prevUser) => {
      if (prevUser) {
        const updatedUser = { ...prevUser, ...updates };
        if (updates.username) {
          localStorage.setItem('username', updates.username);
        }
        return updatedUser;
      }
      return prevUser;
    });
  };

  const isAuthenticated = !!user;

  return (
    <AuthContext.Provider value={{ user, login, logout, updateUserData, isAuthenticated }}>
      {children}
    </AuthContext.Provider>
  );
};
